import posthog from 'posthog-js';
import type { PosthogEventName } from '@/constants/posthogEvents';
import { useUserStore } from '@store/user';

function hasPosthogApiKey() {
    return Boolean(import.meta.env.VITE_POSTHOG_API_KEY);
}

export function capturePosthogEvent(eventName: PosthogEventName): void {
    if (!hasPosthogApiKey()) {
        return;
    }

    try {
        posthog.capture(eventName);
    } catch (error) {
        console.error('PostHog: failed to capture event', error);
    }
}

export function usePosthog() {
    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;

    if (!apiKey) {
        console.log('PostHog: no API key configured, skipping init');
        return;
    }

    posthog.init(apiKey, {
        api_host: 'https://us.i.posthog.com',
        defaults: '2026-01-30',
        disable_session_recording: true,
        autocapture: false,
        capture_dead_clicks: false,
        capture_performance: false,
    });

    const userStore = useUserStore();

    watch(
        () => [userStore.data?.id, userStore.tenantId] as const,
        ([userId, tenantId]) => {
            if (userId) {
                const currentTenant = userStore.data?.tenants?.find((t) => t.id === parseInt(tenantId));
                posthog.identify(String(userId), {
                    email: userStore.data.email,
                    name: userStore.data.fullname,
                    tenant_id: tenantId,
                    tenant_name: currentTenant?.name,
                });
                if (tenantId) {
                    posthog.group('tenant', String(tenantId), {
                        name: currentTenant?.name,
                    });
                }
            } else {
                posthog.reset();
            }
        },
        { immediate: true },
    );

    return posthog;
}
